import { Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface Match {
  id: string
  home_team: string
  away_team: string
  home_team_logo?: string
  away_team_logo?: string
  scheduled_time: string
  status: string
  league_name?: string
}

interface NextMatchCardProps {
  match?: Match
}

export function NextMatchCard({ match }: NextMatchCardProps) {
  if (!match) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Next Match
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-muted-foreground">No upcoming matches</p>
            <p className="text-sm text-muted-foreground mt-1">
              Join a league to see your next match
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (date.getTime() - now.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString()
  }

  const getTimeRemaining = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = date.getTime() - now.getTime()
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else {
      return `${minutes}m`
    }
  }

  return (
    <Card className="bg-gradient-to-br from-card to-card/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          Next Match
        </CardTitle>
        {match.league_name && (
          <Badge variant="secondary" className="w-fit">
            {match.league_name}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Teams */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {match.home_team_logo && (
                <img 
                  src={match.home_team_logo} 
                  alt={match.home_team} 
                  className="w-8 h-8 object-contain"
                />
              )}
              <span className="font-medium text-lg">{match.home_team}</span>
            </div>
            
            <div className="text-2xl font-bold text-muted-foreground">VS</div>
            
            <div className="flex items-center gap-3">
              <span className="font-medium text-lg">{match.away_team}</span>
              {match.away_team_logo && (
                <img 
                  src={match.away_team_logo} 
                  alt={match.away_team} 
                  className="w-8 h-8 object-contain"
                />
              )}
            </div>
          </div>

          {/* Time Info */}
          <div className="text-center space-y-2">
            <div className="text-sm text-muted-foreground">
              Today, {formatTime(match.scheduled_time)} EST
            </div>
            <div className="text-primary font-mono text-lg">
              {getTimeRemaining(match.scheduled_time)}
            </div>
            <div className="text-xs text-muted-foreground">
              Time remaining
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}